import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {ProfileMain} from 'src/components/profile-tab-structure/profile-main';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {
    selectAccountLicense,
} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {LicenseUsageLimitsView, useDefaultLicenseLimits, useLicenseLimits} from 'src/modules/usage-limits';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Loading from '@pb/kit-component-loading';
import Typography from '@pb/kit-component-typography';

import {
    AccessToModulesSection,
    useLicenseDataSets,
    usePlatformFeatureModules,
    usePlatformFeatures,
} from './sections/access-to-modules';
import {AccountLimitsSection, useAccountLimits} from './sections/account-limits';

import type {LicenseInfo} from 'src/openapi/admin/models/LicenseInfo';


type Props = {
    licenseId: number,
    accountId: number,
    license: LicenseInfo,
};

const FeaturesAndLimitsTab = ({licenseId, accountId, license}: Props) => {
    const {accountSubmit, accountFieldChange} = useEventTracking();

    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);
    const {addChangedChunk, removeChangedChunk} = useContext(ConfirmationContext);

    const [saveBusy, setSaveBusy] = useState(false);

    const {isFutureDate} = useDateTime();
    const isExpiredLicense = license ? !isFutureDate(license.startDate) && !license.isActive : false;
    const {canEditAccountLimits, canEditAccessToModules} = useAdminAccessRight();
    const canEditLimits = canEditAccountLimits(accountId) && !isExpiredLicense;
    const canEditModules = canEditAccessToModules(accountId) && !isExpiredLicense;

    const {
        isLoading: isDefaultUsageLimitsLoading,
        loadingError: isDefaultUsageLimitsLoadingError,
        data: defaultUsageLimits,
    } = useDefaultLicenseLimits({licenseType: license.typeCode});

    const {
        isLoading: usageLimitsLoading,
        loadingError: usageLimitsLoadingError,
        data: usageLimits,
        setData: setUsageLimits,
        isChanged: isUsageLimitsChanged,
        saveChanges: saveUsageLimits,
        discardChanges: discardUsageLimits,
    } = useLicenseLimits({licenseId});

    const {
        isLoading: accountLimitsLoading,
        loadingError: accountLimitsLoadingError,
        isChanged: isAccountLimitsChanged,
        data: accountLimits,
        setData: setAccountLimits,
        saveChanges: saveAccountLimits,
        discardChanges: discardAccountLimits,
    } = useAccountLimits({accountId});

    const {
        loading: dataSetsLoading,
        dataSets,
        setDataSets,
        dataSetsTree,
        isDefaultDataSets,
        isInitialDataSets,
        onSaveDataSets,
        onDiscardDataSets,
        onResetDataSets,
    } = useLicenseDataSets({
        licenseId,
        licenseType: license.typeCode,
        disabled: isExpiredLicense || !canEditModules,
    });

    const {
        loading: platformFeatureModulesLoading,
        platformFeatureModulesTree,
        platformFeatureModules,
        setPlatformFeatureModules,
        isDefaultPlatformFeatureModules,
        isInitialPlatformFeatureModules,
        onSavePlatformFeatureModules,
        onDiscardPlatformFeatureModules,
        onResetPlatformFeatureModules,
    } = usePlatformFeatureModules({
        accountId,
        licenseId,
        licenseType: license.typeCode,
        disabled: isExpiredLicense || !canEditModules,
    });

    const {
        loading: platformFeaturesLoading,
        pluginsAppsTree,
        exportOptionsTree,
        platformFeatures,
        setPlatformFeatures,
        isDefaultPlatformFeatures,
        isInitialPlatformFeatures,
        onSavePlatformFeatures,
        onDiscardPlatformFeatures,
        onResetPlatformFeatures,
    } = usePlatformFeatures({
        accountId,
        licenseId,
        licenseType: license.typeCode,
        disabled: isExpiredLicense || !canEditModules,
    });

    useEffect(() => {
        const changed =
            isUsageLimitsChanged || isAccountLimitsChanged ||
            !isInitialDataSets || !isInitialPlatformFeatureModules || !isInitialPlatformFeatures;

        (changed ? addChangedChunk : removeChangedChunk)('features-and-limits');
    }, [
        addChangedChunk, removeChangedChunk,
        isUsageLimitsChanged, isAccountLimitsChanged,
        isInitialDataSets, isInitialPlatformFeatureModules, isInitialPlatformFeatures,
    ]);

    const onSaveChanges = useCallback(async () => {
        accountSubmit({submit: true, place: 'Features & Limits'});
        const callResults = [];

        if (!dataSets.length && !platformFeatureModules.length) {
            setErrorMessage(VALIDATION_FAILED);
            return;
        }

        setSaveBusy(true);

        if (isUsageLimitsChanged) {
            callResults.push(await saveUsageLimits());
        }

        if (isAccountLimitsChanged) {
            callResults.push(await saveAccountLimits());
        }

        if (!dataSets.length) {
            if (!isInitialPlatformFeatureModules) {
                callResults.push(await onSavePlatformFeatureModules());
            }

            if (!isInitialDataSets) {
                callResults.push(await onSaveDataSets());
            }
        } else {
            if (!isInitialDataSets) {
                callResults.push(await onSaveDataSets());
            }

            if (!isInitialPlatformFeatureModules) {
                callResults.push(await onSavePlatformFeatureModules());
            }
        }

        if (!isInitialPlatformFeatures) {
            callResults.push(await onSavePlatformFeatures());
        }

        const errors = [...new Set(callResults.filter(Boolean))];
        setSaveBusy(false);

        if (errors.length) {
            setErrorMessage(errors.join(' '));
        } else {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        }
    }, [
        isUsageLimitsChanged,
        isAccountLimitsChanged,
        isInitialDataSets,
        isInitialPlatformFeatureModules,
        isInitialPlatformFeatures,
        onSavePlatformFeatureModules,
        onSavePlatformFeatures,
        saveUsageLimits,
        saveAccountLimits,
        onSaveDataSets,
        dataSets,
        platformFeatureModules,
        accountSubmit,
        setErrorMessage,
        setSuccessMessage,
    ]);

    const onDiscardChanges = useCallback(() => {
        accountSubmit({submit: false, place: 'Features & Limits'});

        discardUsageLimits();
        discardAccountLimits();
        onDiscardDataSets();
        onDiscardPlatformFeatureModules();
        onDiscardPlatformFeatures();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [
        accountSubmit, setSuccessMessage,
        discardAccountLimits, discardUsageLimits,
        onDiscardDataSets, onDiscardPlatformFeatureModules, onDiscardPlatformFeatures,
    ]);

    const onResetModules = useCallback(() => {
        onResetDataSets();
        onResetPlatformFeatureModules();
        onResetPlatformFeatures();
    }, [onResetDataSets, onResetPlatformFeatureModules, onResetPlatformFeatures]);

    const isSaveDiscardDisabled = useMemo(() => (
        !isUsageLimitsChanged && !isAccountLimitsChanged &&
        isInitialDataSets && isInitialPlatformFeatureModules && isInitialPlatformFeatures
    ), [
        isUsageLimitsChanged, isAccountLimitsChanged,
        isInitialDataSets, isInitialPlatformFeatureModules, isInitialPlatformFeatures,
    ]);

    const isDefaultModules = useMemo(() => (
        isDefaultDataSets && isDefaultPlatformFeatureModules && isDefaultPlatformFeatures
    ), [isDefaultDataSets, isDefaultPlatformFeatureModules, isDefaultPlatformFeatures]);

    const onFieldChange = (section: string) => (parameterName: string) => {
        accountFieldChange({
            parameterName,
            viewType: 'Existed',
            section,
        });
    };

    return (
        <ProfileMain header={(
            <Item grow={1}>
                <Group justify="space-between">
                    <Typography
                        fontWeight="semi-bold"
                        variant="heading-level-3">
                        Features and Limits
                    </Typography>
                    {(canEditLimits || canEditModules) ? (
                        <Group>
                            <Button
                                variant="Type.SECONDARY"
                                disabled={isSaveDiscardDisabled}
                                onClick={onDiscardChanges}>
                                Discard Changes
                            </Button>
                            <Button
                                busy={saveBusy}
                                variant="Type.PRIMARY"
                                disabled={isSaveDiscardDisabled}
                                onClick={onSaveChanges}>
                                Save Changes
                            </Button>
                        </Group>
                    ) : null}
                </Group>
            </Item>
        )}>
            <LicenseUsageLimitsView
                disabled={!canEditLimits}
                licenseType={license?.typeCode}
                loading={usageLimitsLoading || isDefaultUsageLimitsLoading}
                loadingError={usageLimitsLoadingError || isDefaultUsageLimitsLoadingError}
                isDefaultUsageLimits={isDeepEqual({prev: defaultUsageLimits, current: usageLimits})}
                usageLimits={usageLimits}
                defaultUsageLimits={defaultUsageLimits}
                onChange={setUsageLimits}
                onFieldChange={onFieldChange('Usage Limits')}
            />
            <AccountLimitsSection
                loadingError={accountLimitsLoadingError}
                disabled={!canEditLimits}
                loading={accountLimitsLoading}
                accountLimits={accountLimits}
                onChange={setAccountLimits}
                onFieldChange={onFieldChange('Account Limits')}
            />
            <AccessToModulesSection {...{
                disabled: !canEditModules,
                isDefaultModules,
                licenseType: license?.typeCode,

                dataSetsLoading,
                dataSetsTree,
                dataSets,
                setDataSets,

                platformFeatureModulesLoading,
                platformFeatureModulesTree,
                platformFeatures,
                setPlatformFeatures,

                platformFeaturesLoading,
                pluginsAppsTree,
                exportOptionsTree,
                platformFeatureModules,
                setPlatformFeatureModules,

                onFieldChange: onFieldChange('Access to Modules'),
                onResetModules,
            }}
            />
        </ProfileMain>
    );
};

export const FeaturesAndLimitsTabWrapper = () => {
    const params = useParams<{licenseId?: string, accountId: string}>();
    const licenseId = params.licenseId ? +params.licenseId : undefined;
    const accountId = params.accountId ? +params.accountId : undefined;
    const license = useSelector(selectAccountLicense);

    if (!license || !licenseId || !accountId) {
        return <Loading fast={true}/>;
    }

    return (
        <FeaturesAndLimitsTab
            accountId={accountId}
            licenseId={licenseId}
            license={license}
        />
    );
};
